// src/pages/SignUpPage.js
import React, { useState } from 'react';
import {
    UserIcon,
    EnvelopeIcon,
    LockClosedIcon,
    BuildingOfficeIcon,
    SparklesIcon,
    TagIcon,
} from '@heroicons/react/24/outline';
import {ArrowRightIcon, ChevronDownIcon} from '@heroicons/react/20/solid';
import {auth, db} from "../../firebase";
import {useNavigate} from "react-router-dom";
import {toast} from "react-toastify";
import * as emailjs from "@emailjs/browser"; // For a small arrow icon

const SignUpPage = () => {
    const navigate = useNavigate()

    const [fullName, setFullName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    // const [businessName, setBusinessName] = useState('');
    // const [industry, setIndustry] = useState('');
    const [agreeToTerms, setAgreeToTerms] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [user, setUser] = useState({
        from_name: "",
        user_email: ""
    })

    // const industries = [
    //     { value: '', label: 'Select your industry' },
    //     { value: 'retail', label: 'Retail' },
    //     { value: 'food_beverage', label: 'Food & Beverage' },
    //     { value: 'beauty_salon', label: 'Beauty & Salon' },
    //     { value: 'ecommerce', label: 'E-commerce' },
    //     { value: 'services', label: 'Services' },
    //     { value: 'other', label: 'Other' },
    // ];

    const sendEmail = (fullName, email) => {

        const updatedUser = { ...user, from_name: fullName, user_email: email };

        const serviceID = 'service_3w46q8n'; // from EmailJS dashboard
        const templateID = 'template_tglp83g'; // from EmailJS dashboard
        const userID = 'DzSa67yBm-zkoEGVF'; // from EmailJS dashboard

        // Send the email with the QR code data as a parameter
        emailjs.send(serviceID, templateID, updatedUser, userID)
            .then((response) => {
                // toast.success(`Loyalty welcome email sent: ${response.status} ${response.text}`);
                toast.success("Account created successfully.");
                navigate("/login")
            })
            .catch((error) => {
                toast.error(`Failed to send email: ${error.message}`);
            });
    };

    const handleSignUp = async (e) => {
        e.preventDefault();
        setError(''); // Clear previous errors
        setLoading(true);

        // Basic client-side validation
        if (!fullName || !email || !password || !confirmPassword) {
            setError('Please fill in all required fields.');
            setLoading(false);
            return;
        }
        if (password.length < 8) {
            setError('Password must be at least 8 characters long.');
            setLoading(false);
            return;
        }
        if (password !== confirmPassword) {
            setError('Passwords do not match.');
            setLoading(false);
            return;
        }
        if (!agreeToTerms) {
            setError('You must agree to the Terms of Service and Privacy Policy.');
            setLoading(false);
            return;
        }

        auth.createUserWithEmailAndPassword(email, password).then((authUser) => {
            if (authUser) {
                db.ref().child(`localRewards/account/${authUser.user.uid}`).update({
                    fullName: fullName,
                    email: email,
                    userId: authUser.user.uid,
                    createdOn: new Date().getTime()
                }).then(() => {
                    sendEmail(fullName, email)
                }).catch((error) => {
                    toast.error(error.message);
                })
            } else {
                toast.error("Something went wrong. Cannot sign you up.");
            }
        }).catch((err) => {
            toast.error(err.message);
        })
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl w-full bg-white rounded-xl shadow-2xl p-8 space-y-8 animate-fade-in-up">
                <div className="max-w-md mx-auto">
                    <div>
                        {/* LocalRewards Logo */}
                        <div className="flex justify-center mb-6">
                            <SparklesIcon className="h-16 w-16 text-indigo-600"/>
                        </div>
                        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                            Create your LocalRewards Account
                        </h2>
                        <p className="mt-2 text-center text-sm text-gray-600">
                            Start building customer loyalty, effortlessly.
                        </p>
                    </div>

                    <form className="mt-8 space-y-6" onSubmit={handleSignUp}>
                        {error && (
                            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md relative"
                                 role="alert">
                                <strong className="font-bold">Error:</strong>
                                <span className="block sm:inline ml-2">{error}</span>
                            </div>
                        )}

                        <div className="rounded-md shadow-sm -space-y-px">
                            {/* Full Name */}
                            <div>
                                <label htmlFor="full-name" className="sr-only">Full Name</label>
                                <div className="relative">
                                    <div
                                        className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <UserIcon className="h-5 w-5 text-gray-400 z-50" aria-hidden="true"/>
                                    </div>
                                    <input
                                        id="full-name"
                                        name="full-name"
                                        type="text"
                                        autoComplete="name"
                                        required
                                        className="appearance-none rounded-none relative block w-full px-10 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                        placeholder="Full Name"
                                        value={fullName}
                                        onChange={(e) => setFullName(e.target.value)}
                                    />
                                </div>
                            </div>
                            {/* Business Email */}
                            <div>
                                <label htmlFor="business-email" className="sr-only">Business Email</label>
                                <div className="relative">
                                    <div
                                        className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <EnvelopeIcon className="h-5 w-5 text-gray-400 z-50" aria-hidden="true"/>
                                    </div>
                                    <input
                                        id="email"
                                        name="email"
                                        type="email"
                                        autoComplete="email"
                                        required
                                        className="appearance-none rounded-none relative block w-full px-10 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                        placeholder="Email Address"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                    />
                                </div>
                            </div>
                            {/* Password */}
                            <div>
                                <label htmlFor="password" className="sr-only">Password</label>
                                <div className="relative">
                                    <div
                                        className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <LockClosedIcon className="h-5 w-5 text-gray-400 z-50" aria-hidden="true"/>
                                    </div>
                                    <input
                                        id="password"
                                        name="password"
                                        type="password"
                                        autoComplete="new-password"
                                        required
                                        className="appearance-none rounded-none relative block w-full px-10 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                        placeholder="Password (min. 8 characters)"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                    />
                                </div>
                            </div>
                            {/* Confirm Password */}
                            <div>
                                <label htmlFor="confirm-password" className="sr-only">Confirm Password</label>
                                <div className="relative">
                                    <div
                                        className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <LockClosedIcon className="h-5 w-5 text-gray-400 z-50" aria-hidden="true"/>
                                    </div>
                                    <input
                                        id="confirm-password"
                                        name="confirm-password"
                                        type="password"
                                        autoComplete="new-password"
                                        required
                                        className="appearance-none rounded-none relative block w-full px-10 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                        placeholder="Confirm Password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        {/*<div className="rounded-md shadow-sm">*/}
                        {/*    /!* Business Name *!/*/}
                        {/*    <div>*/}
                        {/*        <label htmlFor="business-name" className="sr-only">Business Name</label>*/}
                        {/*        <div className="relative">*/}
                        {/*            <div*/}
                        {/*                className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">*/}
                        {/*                <BuildingOfficeIcon className="h-5 w-5 text-gray-400 z-50" aria-hidden="true"/>*/}
                        {/*            </div>*/}
                        {/*            <input*/}
                        {/*                id="business-name"*/}
                        {/*                name="business-name"*/}
                        {/*                type="text"*/}
                        {/*                required*/}
                        {/*                className="appearance-none relative block w-full px-10 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"*/}
                        {/*                placeholder="Your Business Name"*/}
                        {/*                value={businessName}*/}
                        {/*                onChange={(e) => setBusinessName(e.target.value)}*/}
                        {/*            />*/}
                        {/*        </div>*/}
                        {/*    </div>*/}
                        {/*    /!* Industry (Optional) *!/*/}
                        {/*    <div>*/}
                        {/*        <label htmlFor="industry" className="sr-only">Industry</label>*/}
                        {/*        <div className="relative">*/}
                        {/*            <div*/}
                        {/*                className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">*/}
                        {/*                <TagIcon className="h-5 w-5 text-gray-400 z-50" aria-hidden="true"/>*/}
                        {/*            </div>*/}
                        {/*            <select*/}
                        {/*                id="industry"*/}
                        {/*                name="industry"*/}
                        {/*                className="appearance-none relative block w-full px-10 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white"*/}
                        {/*                value={industry}*/}
                        {/*                onChange={(e) => setIndustry(e.target.value)}*/}
                        {/*            >*/}
                        {/*                {industries.map((option) => (*/}
                        {/*                    <option key={option.value} value={option.value}>*/}
                        {/*                        {option.label}*/}
                        {/*                    </option>*/}
                        {/*                ))}*/}
                        {/*            </select>*/}
                        {/*            <div*/}
                        {/*                className="absolute inset-y-0 right-0 pl-3 flex items-center pointer-events-none">*/}
                        {/*                <ChevronDownIcon className="h-5 w-5 mr-2 text-gray-400 z-50" aria-hidden="true"/>*/}
                        {/*            </div>*/}
                        {/*        </div>*/}
                        {/*    </div>*/}
                        {/*</div>*/}

                        {/* Terms & Privacy */}
                        <div className="flex items-center">
                            <input
                                id="agree-to-terms"
                                name="agree-to-terms"
                                type="checkbox"
                                required
                                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                checked={agreeToTerms}
                                onChange={(e) => setAgreeToTerms(e.target.checked)}
                            />
                            <label htmlFor="agree-to-terms" className="ml-2 block text-sm text-gray-900">
                                I agree to the{' '}
                                <a href="/terms" className="font-medium text-indigo-600 hover:text-indigo-500">
                                    Terms of Service
                                </a>{' '}
                                and{' '}
                                <a href="/privacy" className="font-medium text-indigo-600 hover:text-indigo-500">
                                    Privacy Policy
                                </a>.
                            </label>
                        </div>

                        <div>
                            <button
                                type="submit"
                                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200"
                                disabled={loading}
                            >
                                {loading ? 'Creating Account...' : 'Sign Up'}
                                {loading ? (
                                    <svg className="animate-spin -mr-1 ml-3 h-5 w-5 text-white"
                                         xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                                strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor"
                                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                ) : (
                                    <ArrowRightIcon
                                        className="-mr-1 ml-2 h-5 w-5 text-indigo-300 group-hover:text-white z-50"
                                        aria-hidden="true"/>
                                )}
                            </button>
                        </div>
                    </form>

                    <div className="mt-6 text-center">
                        <p className="text-sm text-gray-600">
                            Already have an account?{' '}
                            <a href="/login" className="font-medium text-indigo-600 hover:text-indigo-500">
                                Sign in
                            </a>
                        </p>
                    </div>

                    {/* Optional: Social Sign Up Section */}
                    <div className="mt-6">
                        <div className="relative">
                            <div className="absolute inset-0 flex items-center" aria-hidden="true">
                                <div className="w-full border-t border-gray-300"/>
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="px-2 bg-white text-gray-500">Or sign up with</span>
                            </div>
                        </div>

                        <div className="mt-6 grid grid-cols-2 gap-3">
                            <div>
                                <a
                                    href="#"
                                    className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                                >
                                    <img
                                        src="https://upload.wikimedia.org/wikipedia/commons/4/4a/Logo_Google_2020_-_Icon_only.svg"
                                        className="h-5 w-5 mr-2" alt="Google"/>
                                    Google
                                </a>
                            </div>
                            <div>
                                <a
                                    href="#"
                                    className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                                >
                                    <img src="https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg"
                                         className="h-5 w-5 mr-2" alt="Microsoft"/>
                                    Microsoft
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SignUpPage;